import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { getConfig } from '../utils/config';
import { authenticatedFetch } from '../utils/authService';

// Validation function as specified in the instructions
const validateTicketFormat = (ticketCode) => {
    const regex = /^\d+-\d+-\d+$/;
    if (!ticketCode || typeof ticketCode !== 'string' || !regex.test(ticketCode)) {
        return { valid: false, error: 'Formato inválido. Use el formato: sucursal-venta-folio' };
    }
    
    const [sucursal, venta, timestamp] = ticketCode.split('-');
    
    // Validate that all parts are numeric
    if (isNaN(sucursal) || isNaN(venta) || isNaN(timestamp)) {
        return { valid: false, error: 'Todos los valores deben ser numéricos' };
    }
    
    // Validate timestamp
    const date = new Date(parseInt(timestamp) * 1000);
    
    if (isNaN(date.getTime())) {
        return { valid: false, error: 'Timestamp inválido' };
    }
    
    // Check if timestamp has a future date
    const now = new Date();
    
    if (date > now) {
        return { valid: false, error: 'El ticket tiene una fecha futura inválida' };
    }
    
    // Validate that the ticket is from the current month
    // if (date.getMonth() !== now.getMonth() || date.getFullYear() !== now.getFullYear()) {
    //     return { valid: false, error: 'TICKET VENCIDO' };
    // }
    
    return { 
        valid: true, 
        data: {
            sucursal: parseInt(sucursal),
            venta: parseInt(venta),
            timestamp: parseInt(timestamp),
            date: date
        }
    };
};

// Customer data will be fetched as part of the sale details
// No need for a separate function to check customer existence

// Function to validate the ticket against the API endpoint
// According to the API docs, this calls: GET /tickets/{code}
const validateTicketInDatabase = async (ticketCode) => {
    // Get config from our utility
    const config = getConfig();
    const API_BASE_URL = config.apiBaseUrl;
    
    // Always log API requests for debugging (regardless of debug setting)
    console.log(`API Request to: ${API_BASE_URL}/tickets/${ticketCode}`);
    console.log('Config:', config);
    
    try {
        // First API Call: Check if ticket exists
        const response = await authenticatedFetch(`${API_BASE_URL}/tickets/${ticketCode}`, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            }
        });
        
        // Process response based on status
        if (response.status === 404) {
            return {
                success: false,
                error: 'La venta no se encuentra en nuestros registros. Verifique el código del ticket.'
            };
        }
        
        if (response.status === 500) {
            return {
                success: false,
                error: 'Si requiere factura por favor contactar a soporte técnico  (55) 4620 0804.'
            };
        }
        
        if (!response.ok) {
            return {
                success: false,
                error: 'Ocurrió un error al validar el ticket. Intente nuevamente.'
            };
        }
        
        // Parse the response data
        const data = await response.json();
        
        // Log full response data to debug
        console.log('API Response from /tickets/{code}:', JSON.stringify(data, null, 2));
        
        // Initialize the ticket data with basic information
        let ticketData = {
            ticket: {
                code: ticketCode,
                data: data
            },
            isExistingInvoice: data.isInvoiced || false,
            alreadyInvoiced: data.isInvoiced || false,
            requiresCustomerData: true,  // Default assumption
            sucursal: data.sucursal,
            venta: data.venta,
            message: data.message
        };
        
        // IMPORTANT: Always try to get sale details regardless of invoice status
        // This is the key fix - ensuring we always have the sale data
        if (data.sucursal && data.venta) {
            console.log(`Making sale details API call: ${API_BASE_URL}/sucursales/${data.sucursal}/ventas/${data.venta}`);
            
            try {
                // Second API Call: Get sale details
                const ventaResponse = await authenticatedFetch(`${API_BASE_URL}/sucursales/${data.sucursal}/ventas/${data.venta}`, {
                    method: 'GET',
                    headers: {
                        'Accept': 'application/json',
                        'Content-Type': 'application/json'
                    }
                });
                
                if (ventaResponse.ok) {
                    const ventaData = await ventaResponse.json();
                    
                    // Debug log the raw API response
                    console.log('API Response - Raw venta data:', JSON.stringify(ventaData, null, 2));
                    
                    // Store the raw venta data
                    ticketData.ventaDetails = ventaData;
                    
                    // Build properly structured sale object
                    const ventaObj = ventaData.venta || {};
                    const productos = ventaData.productos || [];
                    
                    // Calculate total from productos if needed
                    let totalFromProducts = 0;
                    if (productos && productos.length > 0) {
                        totalFromProducts = productos.reduce((sum, item) => sum + (parseFloat(item.importe) || 0), 0);
                    }
                    
                    // Create properly structured sale object for the SaleDetails component
                    const saleObject = {
                        id: ventaObj.id || parseInt(data.venta) || 0,
                        sucursalId: ventaObj.sucursal_id || parseInt(data.sucursal) || 0,
                        sucursalName: ventaObj.sucursal || '',
                        fechaVenta: ventaObj.fecha_hora || new Date().toISOString(),
                        formaPago: ventaObj.forma_pago || '99', // Add the new forma_pago field
                        total: parseFloat(ventaObj.total) || totalFromProducts || 0, // Now ensures total comes from API response
                        productos: Array.isArray(productos) ? productos.map((item, index) => {
                            console.log(`Processing product ${index}:`, JSON.stringify(item, null, 2));
                            
                            // Map products from the new structure
                            const mappedProduct = {
                                id: parseInt(item.id) || 0,
                                nombre: item.producto || 'Producto sin nombre',
                                cantidad: parseInt(item.cantidad) || 0,
                                precioUnitario: parseFloat(item.precio_unitario) || 0,
                                subtotal: parseFloat(item.importe) || 0,
                                importe: parseFloat(item.importe) || 0,
                                descuento: parseFloat(item.descuento) || 0,
                                // Add the new SAT fields
                                claveUnidad: item.clave_unidad || 'H87',
                                claveUnidadDescripcion: item.clave_unidad_descripcion || 'piece',
                                claveProdServ: item.clave_prod_serv || '01010101',
                                productoServicio: item.producto_servicio || 'No Existe en el Catálogo'
                            };
                            
                            console.log(`Mapped product ${index}:`, JSON.stringify(mappedProduct, null, 2));
                            return mappedProduct;
                        }) : []
                    };
                    
                    // Assign the properly formatted sale object
                    ticketData.sale = saleObject;
                    console.log('Structured sale object created:', JSON.stringify(saleObject, null, 2));
                    
                    // If the venta response includes customer data, use it
                    if (ventaData.cliente && ventaData.cliente.success && ventaData.cliente.cliente) {
                        const clienteData = ventaData.cliente.cliente;
                        
                        // Map API cliente structure to expected format based on the new schema
                        const customerObject = {
                            id: parseInt(clienteData.id) || 0,
                            rfc: clienteData.rfc || '',
                            razonSocial: clienteData.razon_social || '',
                            email: clienteData.email || '',
                            telefono: clienteData.telefono || '',
                            codigoPostal: clienteData.cp_fiscal || '',
                            usoCfdi: clienteData.uso_cfdi_clave || 'G03',
                            regimenFiscal: clienteData.regimen_fiscal_clave || '616'
                        };
                        
                        // Special case: If customer ID is 1, consider it as no customer (unregistered)
                        const isCustomerUnregistered = customerObject.id === 1;
                        
                        if (isCustomerUnregistered) {
                            ticketData.existingCustomer = null;
                            ticketData.hasExistingCustomer = false;
                            ticketData.requiresCustomerData = true;
                            console.log('Client ID is 1 - treating as unregistered customer');
                        } else {
                            ticketData.existingCustomer = customerObject;
                            ticketData.hasExistingCustomer = true;
                            ticketData.requiresCustomerData = false;
                            console.log('Customer data found:', JSON.stringify(customerObject, null, 2));
                        }
                    } else {
                        ticketData.requiresCustomerData = true;
                        ticketData.hasExistingCustomer = false;
                    }
                } else {
                    console.error('Error fetching sale details:', ventaResponse.statusText);
                    
                    // Create a default sale object to prevent undefined errors
                    ticketData.sale = {
                        id: parseInt(data.venta) || 0,
                        sucursalId: parseInt(data.sucursal) || 0,
                        fechaVenta: new Date().toISOString(),
                        total: 0,
                        productos: []
                    };
                }
            } catch (ventaError) {
                console.error('Error fetching sale details:', ventaError);
                
                // Create a default sale object to prevent undefined errors
                ticketData.sale = {
                    id: parseInt(data.venta) || 0,
                    sucursalId: parseInt(data.sucursal) || 0,
                    fechaVenta: new Date().toISOString(),
                    total: 0,
                    productos: []
                };
            }
        } else {
            console.warn('Missing sucursal or venta data - cannot fetch sale details');
            
            // Create a default sale object to prevent undefined errors
            ticketData.sale = {
                id: 0,
                sucursalId: 0,
                fechaVenta: new Date().toISOString(),
                total: 0,
                productos: []
            };
        }
        
        // If the ticket has been invoiced, fetch the invoice details
        if (data.isInvoiced) {
            console.log(`Ticket is already invoiced - fetching invoice details`);
            
            try {
                // Third API Call: Get invoice details if already invoiced
                const facturaResponse = await authenticatedFetch(`${API_BASE_URL}/sucursales/${data.sucursal}/ventas/${data.venta}/factura`, {
                    method: 'GET',
                    headers: {
                        'Accept': 'application/json',
                        'Content-Type': 'application/json'
                    }
                });
                
                // Handle 404 - Invoice not found
                if (facturaResponse.status === 404) {
                    console.error('Invoice not found (404) for invoiced ticket');
                    return {
                        success: false,
                        error: 'Factura no encontrada. Contactenos por WhatsApp al (55) 4620 0804 '
                    };
                }
                
                // Handle other non-OK responses
                if (!facturaResponse.ok) {
                    console.error('Error fetching invoice details:', facturaResponse.status, facturaResponse.statusText);
                    return {
                        success: false,
                        error: `Error al obtener los detalles de la factura. Código: ${facturaResponse.status}. Por favor contacte a soporte técnico.`
                    };
                }
                
                if (facturaResponse.ok) {
                    const facturaData = await facturaResponse.json();
                    
                    // Log the raw data to debug
                    console.log('Raw factura data:', JSON.stringify(facturaData, null, 2));
                    
                    // Check if we received the new response format with nested structure
                    // The API returns: { "message": "...", "factura": { "factura": {...}, "cliente": {...}, "download_links": {...} } }
                    if (facturaData.message === 'Factura encontrada' && facturaData.factura) {
                        console.log('New invoice response format detected');
                        
                        // Extract the nested data structure
                        const facturaInfo = facturaData.factura.factura || facturaData.factura;
                        const clienteInfo = facturaData.factura.cliente || facturaData.cliente;
                        const downloadLinks = facturaData.factura.download_links || facturaData.download_links;
                        
                        // Use the new structure directly
                        ticketData.invoice = {
                            // Original fields needed for backward compatibility
                            uuid: facturaInfo.uuid || '',
                            total: parseFloat(facturaInfo.total) || 0,
                            fechaTimbrado: facturaInfo.fecha_timbrado || new Date().toISOString(),
                            // New structure fields
                            factura: facturaInfo,
                            cliente: clienteInfo,
                            download_links: downloadLinks,
                            // Normalize downloadLinks for backward compatibility
                            downloadLinks: {
                                pdf: downloadLinks?.pdf || '#',
                                xml: downloadLinks?.xml || '#'
                            }
                        };
                        
                        // Extract customer data if available
                        if (clienteInfo) {
                            ticketData.existingCustomer = {
                                id: 0, // Not typically available
                                rfc: clienteInfo.rfc || '',
                                razonSocial: clienteInfo.nombre || '',
                                email: clienteInfo.email || '',
                                telefono: '', // Not available in new format
                                codigoPostal: '', // Not available in new format
                                usoCfdi: 'G03', // Default
                                regimenFiscal: '616' // Default
                            };
                            ticketData.hasExistingCustomer = true;
                            ticketData.requiresCustomerData = false;
                        }
                    } else {
                        // Fallback to old format handling
                        console.log('Using old invoice format handling');
                        const invoiceData = {
                            uuid: facturaData.factura.uuid || '',
                            total: parseFloat(facturaData.factura.total) || 0,
                            fechaTimbrado: facturaData.factura.fecha_hora || new Date().toISOString(),
                            downloadLinks: {
                                pdf: `${API_BASE_URL}/sucursales/${data.sucursal}/ventas/${data.venta}/factura?format=pdf`,
                                xml: `${API_BASE_URL}/sucursales/${data.sucursal}/ventas/${data.venta}/factura?format=xml`
                            }
                        };
                        
                        ticketData.invoice = invoiceData;
                        ticketData.rawFacturaData = facturaData.factura;
                        
                        // If we don't have customer data yet but have invoice data, extract it
                        if (facturaData.factura && !ticketData.existingCustomer) {
                            const facturaCustomer = {
                                id: 0, // Not typically available from factura
                                rfc: facturaData.factura.rfc || '',
                                razonSocial: facturaData.factura.razon_social || '',
                                email: facturaData.factura.email || '',
                                telefono: facturaData.factura.telefono || '',
                                codigoPostal: facturaData.factura.cp || '',
                                usoCfdi: facturaData.factura.uso_cfdi || 'G03',
                                regimenFiscal: facturaData.factura.regimen_fiscal || '616'
                            };
                            
                            ticketData.customer = facturaCustomer;
                        }
                    }
                    
                    console.log('Structured invoice data:', JSON.stringify(ticketData.invoice, null, 2));
                }
            } catch (facturaError) {
                console.error('Error fetching invoice details:', facturaError);
                return {
                    success: false,
                    error: 'Error al obtener los detalles de la factura. Por favor contacte a soporte técnico.'
                };
            }
        }
        
        // Final checks - ensure sale object exists
        if (!ticketData.sale) {
            console.warn('Sale object missing - creating default');
            ticketData.sale = {
                id: parseInt(data.venta) || 0,
                sucursalId: parseInt(data.sucursal) || 0,
                fechaVenta: new Date().toISOString(),
                total: 0,
                productos: []
            };
        }
        
        // Log the final data structure being returned
        console.log('FINAL TICKET DATA STRUCTURE:', JSON.stringify(ticketData, null, 2));
        
        return {
            success: true,
            ticketData: ticketData
        };
        
    } catch (error) {
        console.error('Error al validar el ticket:', error);
        return {
            success: false, 
            error: 'Error de conexión. Verifique su conectividad e intente nuevamente.'
        };
    }
};

// getConfig is already imported at the top of the file

const TicketValidator = ({ onTicketValidated, onError }) => {
    const [ticketCode, setTicketCode] = useState('');
    const [isValidating, setIsValidating] = useState(false);
    const [formatError, setFormatError] = useState('');

    // Extraer el parámetro ticket del query string cuando el componente se monta
    useEffect(() => {
        // Reiniciar el estado cuando el componente se monta
        setTicketCode('');
        setFormatError('');
        
        // Obtener los parámetros de la URL
        const urlParams = new URLSearchParams(window.location.search);
        const ticketParam = urlParams.get('ticket');
        
        // Si existe el parámetro ticket, usarlo para rellenar el campo
        if (ticketParam) {
            const trimmedTicket = ticketParam.trim();
            setTicketCode(trimmedTicket);
            console.log('Ticket detectado en URL:', trimmedTicket);
            
            // Validar automáticamente el ticket después de un breve retraso
            // para que el usuario pueda ver que se ha rellenado el campo
            const timer = setTimeout(() => {
                // Validar solo si el formato es correcto
                const formatValidation = validateTicketFormat(trimmedTicket);
                console.log('URL ticket format validation:', formatValidation);
                
                if (formatValidation.valid) {
                    handleValidateTicket(trimmedTicket);
                } else {
                    // Si hay error de formato, mostrarlo
                    setFormatError(formatValidation.error);
                }
            }, 500);
            
            // Limpiar el timer en caso de desmontaje del componente
            return () => clearTimeout(timer);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleTicketCodeChange = (e) => {
        const value = e.target.value;
        setTicketCode(value);
        
        // Clear format error when user starts typing
        if (formatError) {
            setFormatError('');
        }
    };

    const handleValidateTicket = async (codeToValidate = null) => {
        // Usar el código proporcionado o el del estado
        const code = codeToValidate || ticketCode.trim();
        
        // First validate format
        const formatValidation = validateTicketFormat(code);
        
        if (!formatValidation.valid) {
            setFormatError(formatValidation.error);
            return;
        }

        setIsValidating(true);
        setFormatError('');

        try {
            // Call real API endpoint for ticket validation
            const result = await validateTicketInDatabase(code);
            
            if (!result.success) {
                onError(result.error || 'Error al validar el ticket. Intente nuevamente.');
                setIsValidating(false);
                return;
            }

            // The validateTicketInDatabase function now returns a complete and well-structured ticketData object
            // We just need to add the format validation data
            // No need to reconstruct the entire object
            const ticketData = {
                ...result.ticketData,
                ticket: {
                    ...result.ticketData.ticket,
                    data: {
                        ...result.ticketData.ticket.data,
                        ...formatValidation.data
                    }
                }
            };

            // Always log the final data being passed to parent
            console.log('FINAL DATA STRUCTURE - Passing to parent:', JSON.stringify(ticketData, null, 2));
            
            // Final verification checks
            if (!ticketData.sale) {
                console.error('Critical Error: Sale object is missing in final data structure!');
                
                // Create a minimal default sale to avoid errors
                ticketData.sale = {
                    id: parseInt(ticketData.venta) || 0,
                    sucursalId: parseInt(ticketData.sucursal) || 0,
                    fechaVenta: new Date().toISOString(),
                    total: 0,
                    productos: []
                };
            } else {
                // Verify the sale object structure
                console.log('SALE OBJECT VERIFICATION:', {
                    id: ticketData.sale.id,
                    sucursalId: ticketData.sale.sucursalId,
                    fechaVenta: ticketData.sale.fechaVenta,
                    productos: Array.isArray(ticketData.sale.productos) ? 
                        `Array with ${ticketData.sale.productos.length} items` : 
                        'Not an array or undefined'
                });
            }
            
            onTicketValidated(ticketData);
            
        } catch (error) {
            console.error('Error validating ticket:', error);
            onError(error.message || 'Error al conectar con el servidor. Por favor intente nuevamente.');
        } finally {
            setIsValidating(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !isValidating && ticketCode.trim()) {
            e.preventDefault();
            handleValidateTicket();
        }
    };

    return (
        <div className="ticket-validator">
            <div className="validator-content">
                <h2>Ingrese el código de su ticket</h2>
                <p className="instructions">
                    Encontrará el código en su ticket de compra con el formato:
                </p>
                <p className="example">
                    Ejemplo: <code>2-12345-1756930919</code>
                </p>

                <div className="input-section">
                    <div className="input-group">
                        <label htmlFor="ticketCode">Código del ticket:</label>
                        <input
                            id="ticketCode"
                            type="text"
                            value={ticketCode}
                            onChange={handleTicketCodeChange}
                            onKeyDown={handleKeyPress}
                            placeholder="Ej: 2-12345-1756930919"
                            disabled={isValidating}
                            className={formatError ? 'input-error' : ''}
                        />
                        
                        {formatError && (
                            <div className="format-error">
                                {formatError}
                            </div>
                        )}
                    </div>

                    <button
                        onClick={() => handleValidateTicket()}
                        disabled={!ticketCode.trim() || isValidating}
                        className="btn-primary validate-btn"
                    >
                        {isValidating ? 'Validando...' : 'Validar Ticket'}
                    </button>
                </div>

                <div className="help-section">
                    <h3>¿Dónde encuentro mi código de ticket?</h3>
                    <ul>
                        <li>En la parte inferior de su ticket de compra</li>
                        <li>Formato: números separados por guiones (-)</li>
                        <li>Si no encuentra su código, contacte a nuestro equipo de soporte</li>
                    </ul>
                    <div className="ticket-example">
                        <img 
                            src="/images/ticket.png" 
                            alt="Ejemplo de ticket con el código resaltado" 
                            className="ticket-image" 
                        />
                    </div>
                </div>

                {/* Format help */}
                {/* <div className="format-help" style={{ marginTop: '2rem', padding: '1rem', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                    <h4>Formato del código de ticket:</h4>
                    <p style={{ fontSize: '0.9rem' }}>
                        El código del ticket tiene el formato: <strong>sucursal-folio-timestamp</strong><br/>
                        Ejemplo: <code>2-12345-1756930919</code><br/>
                        Donde:
                    </p>
                    <ul style={{ fontSize: '0.9rem' }}>
                        <li><strong>sucursal:</strong> Identificador numérico de la sucursal</li>
                        <li><strong>folio:</strong> Número de folio de la venta</li>
                        <li><strong>timestamp:</strong> Fecha y hora de la venta en formato Unix (segundos)</li>
                    </ul>
                </div> */}
            </div>
        </div>
    );
};

TicketValidator.propTypes = {
    onTicketValidated: PropTypes.func.isRequired,
    onError: PropTypes.func.isRequired
};

export default TicketValidator;